import React from 'react'
import { Radio, Space, Text, Textarea, Tooltip } from '@mantine/core'
import { ClockIcon } from '@radix-ui/react-icons'

import { TimeField } from '@/components/common/TimeField'
import { CrFormHandle } from '@/components/Event/CrForm/CrFormStep'
import { isOrderableResult } from '@/components/Event/Feedback/validation'
import {
  CNA_TYPE_OTHER_TEXT_CHAR_LIMIT,
  DBFContact,
  DepositionResult,
  TurnaroundHours,
} from '@/constants'
import { Event, EventResult } from '@/types'

// Generic event-result + CNA options (not SA-specific). `description` renders on the
// result radios.
const RESULT_OPTIONS = [
  {
    value: DepositionResult.COMPLETED,
    label: 'Completed',
    description: 'The questioning was completed',
  },
  {
    value: DepositionResult.COMPLETED_NO_SHOW,
    label: 'Completed – Witness No Show',
    description:
      'The witness did not show and the client wishes to go on the record to record the no show',
  },
  {
    value: DepositionResult.CANCELLED,
    label: 'Canceled or Rescheduled',
    description:
      'Informed that the event is not needed for the scheduled start time',
  },
  {
    value: DepositionResult.CNA,
    label: 'CNA',
    description: `The ${DBFContact.NAME} client requested for a copy of the certificate of non-appearance`,
  },
]

const CNA_TYPE_OPTIONS = [
  { value: 'witness_no_show', label: 'Witness No-Show' },
  { value: 'witness_and_client_no_show', label: 'Witness and Client No-Show' },
  { value: 'opposing_counsel_no_show', label: 'Opposing Counsel No-Show' },
  { value: 'other', label: 'Other (All attendees are present)' },
]

// The event-result radio + Certificate-of-Non-Appearance sub-form, shared by the base
// feedback form and the staged CR Form's Closing step. The wiring (result side-effects,
// CNA field paths, validation errors) lives here once.
export const EventResultFields: React.FC<{
  event: Event
  form: CrFormHandle
  witnessIndex: number
}> = ({ event, form, witnessIndex }) => {
  const prefix = `events_feedbacks.${witnessIndex}`
  const values = form.values.events_feedbacks?.[witnessIndex]

  const setField = (field: string, value: unknown) =>
    form.setFieldValue(`${prefix}.${field}`, value)

  // Keep the transcript fields consistent with the chosen result (mirrors both call
  // sites' original side-effects).
  const setResult = (value: EventResult | null) => {
    if (!value) {
      return
    }
    const isKindOfCompleted =
      value === 'completed' || value === 'completed_no_show'
    setField(
      'transcript_ordered',
      isKindOfCompleted ? event.is_transcript_order_required : false,
    )
    setField(
      'turnaround_hours',
      isKindOfCompleted
        ? event.is_transcript_order_required
          ? TurnaroundHours.SEVEN_DAYS
          : TurnaroundHours.ROUGH_DRAFT
        : null,
    )
    setField(
      'transcript_orders',
      isOrderableResult(value, event.cna_transcript_orders_enabled)
        ? values?.transcript_orders ?? null
        : null,
    )
    setField('result', value)
  }

  const resultOptions =
    event.type === 'hearing'
      ? RESULT_OPTIONS.filter(option => option.value !== DepositionResult.CNA)
      : RESULT_OPTIONS

  const cnaFields = (
    <>
      <Radio.Group
        required
        label="Certificate of Non-Appearance"
        placeholder="Pick one"
        error={form.errors[`${prefix}.cna_type`]}
        value={values?.cna_type ?? undefined}
        onChange={value => {
          setField('cna_type', value)
          setField(
            'cna_type_other_text',
            value === 'other' ? values?.cna_type_other_text : null,
          )
        }}
      >
        {CNA_TYPE_OPTIONS.map(option => (
          <Radio
            my="sm"
            key={option.value}
            value={option.value}
            label={<Text size="sm">{option.label}</Text>}
          />
        ))}
      </Radio.Group>

      {values?.cna_type === 'other' && (
        <Textarea
          label={
            <>
              <Text component="span" size="sm">
                Specify the reason{' '}
              </Text>
              <Text
                component="span"
                size="sm"
                sx={theme => ({ color: theme.colors.gray[6] })}
              >
                (will be visible on certificate)
              </Text>
            </>
          }
          description={`Used ${
            (values?.cna_type_other_text || '').length
          } out of ${CNA_TYPE_OTHER_TEXT_CHAR_LIMIT} available characters`}
          error={form.errors[`${prefix}.cna_type_other_text`]}
          value={values?.cna_type_other_text ?? ''}
          onChange={event =>
            setField(
              'cna_type_other_text',
              event.currentTarget.value.slice(
                0,
                CNA_TYPE_OTHER_TEXT_CHAR_LIMIT,
              ),
            )
          }
          minRows={3}
          autosize
        />
      )}

      <TimeField
        required
        label="Time of declaration of CNA"
        description="In your local time"
        error={form.errors[`${prefix}.cna_declared_at`]}
        value={values?.cna_declared_at ?? undefined}
        onChange={dateTime => setField('cna_declared_at', dateTime ?? null)}
        rightSection={
          <Tooltip label="Set to current time">
            <div>
              <ClockIcon
                color="gray"
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  setField('cna_declared_at', new Date().toISOString())
                }
              />
              <Space w="xl" />
            </div>
          </Tooltip>
        }
        className="w-1/2"
      />
    </>
  )

  return (
    <>
      <Radio.Group
        label="How did the event end?"
        required
        error={form.errors[`${prefix}.result`]}
        value={values?.result ?? undefined}
        onChange={value => setResult(value as EventResult)}
      >
        {resultOptions.map(option => (
          <Radio
            key={option.value}
            my="sm"
            value={option.value}
            label={
              <>
                <Text weight={500} size="sm">
                  {option.label}
                </Text>
                <Text color="gray" size="xs">
                  {option.description}
                </Text>
              </>
            }
          />
        ))}
      </Radio.Group>

      {values?.result === 'canceled_cna' ? cnaFields : null}
    </>
  )
}
